import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, catchError, finalize, tap, EMPTY } from 'rxjs';
import {
  LoadPaymentManagementActive,
  SetPaymentManagementInactive,
} from '@layout/payment-management-active/store/actions/payment-management-active.actions';
import { PaymentManagementActiveService } from '@layout/payment-management-active/services/payment-management-active.service';

interface PaymentManagementActiveStateModel {
  isPaymentManagementActive?: boolean;
  error: boolean;
  loading: boolean;
}

@State<PaymentManagementActiveStateModel>({
  name: 'paymentManagementActive',
  defaults: {
    loading: false,
    error: false,
  },
})
@Injectable()
export class PaymentManagementActiveState {
  private readonly paymentManagementActiveService = inject(PaymentManagementActiveService);

  @Selector()
  static isPaymentManagementActive({ isPaymentManagementActive }: PaymentManagementActiveStateModel): boolean | undefined {
    return isPaymentManagementActive;
  }

  @Selector()
  static loading({ loading }: PaymentManagementActiveStateModel): boolean {
    return loading;
  }

  @Selector()
  static error({ error }: PaymentManagementActiveStateModel): boolean {
    return error;
  }

  @Action(LoadPaymentManagementActive)
  loadPaymentManagementActive(context: StateContext<PaymentManagementActiveStateModel>): Observable<boolean> {
    const { isPaymentManagementActive, loading, error } = context.getState();

    if (loading) {
      return EMPTY;
    }

    if (isPaymentManagementActive !== undefined && !error) {
      return EMPTY;
    }

    context.patchState({ loading: true });

    return this.paymentManagementActiveService.getPaymentManagementActive().pipe(
      tap(isActive => context.patchState({ isPaymentManagementActive: isActive, error: false })),
      catchError(() => {
        context.patchState({ isPaymentManagementActive: false, error: true });
        return EMPTY;
      }),
      finalize(() => context.patchState({ loading: false }))
    );
  }

  @Action(SetPaymentManagementInactive)
  setPaymentManagementInactive(context: StateContext<PaymentManagementActiveStateModel>): void {
    context.patchState({ isPaymentManagementActive: false });
  }
}
